import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { IMAGES, PLACEHOLDERS } from '@/shared/lib/images';

interface SplashScreenProps {
  onComplete?: () => void;
}

export const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      onAnimationComplete={onComplete}
    >
      {/* Logo with pulse animation */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }}
        className="relative"
      >
        {/* Blur placeholder for instant display */}
        <div 
          className="absolute inset-0 w-32 h-32 rounded-full bg-primary/20 blur-xl"
          aria-hidden="true"
        />
        <img 
          src={IMAGES.logo} 
          alt="AGXL Logo" 
          width={128}
          height={128}
          loading="eager"
          decoding="sync"
          className="w-32 h-32 object-contain relative z-10"
        />
      </motion.div>

      {/* App name */}
      <motion.h1
        className="mt-6 text-2xl font-bold text-foreground"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        AGXL
      </motion.h1>

      {/* Tagline */}
      <motion.p
        className="mt-2 text-sm text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
      >
        {t('splash.tagline', 'Farmer Credit Intelligence')}
      </motion.p>

      {/* Loading indicator */}
      <motion.div
        className="mt-8 flex gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.3 }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-primary"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.15,
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};
